import React, { FormEvent, useState } from 'react';
import {
  // eslint-disable-next-line no-restricted-imports
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  VStack,
  Text,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { FormTextInput } from 'src/components/molecules/formInput/FormTextInput';
import { validateEmail } from 'src/utils/email';
import { CustomerRole } from 'src/contexts/AuthContext';

type InviteUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    email: string;
    role: CustomerRole;
    displayName?: string;
  }) => void;
  isLoading: boolean;
};

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<CustomerRole>(CustomerRole.USER);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isValid = !!email.trim() && validateEmail(email.trim());

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!validateEmail(email.trim())) {
      setError('Invalid email address');
      return;
    }

    onSubmit({
      email: email.trim(),
      role,
      displayName: displayName.trim() || undefined,
    });
  };

  const handleClose = () => {
    setEmail('');
    setRole(CustomerRole.USER);
    setDisplayName('');
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent bg="surface.panel" borderRadius="16px">
        <ModalCloseButton />
        <ModalHeader pb={2} pt={6}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="xl"
            color="content.title">
            Invite New User
          </Text>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody px={6} py={4}>
            <VStack align="stretch" spacing={4}>
              <FormTextInput
                useForm={false}
                type="email"
                label="Email"
                placeholder="colleague@company.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <FormControl isRequired>
                <FormLabel
                  fontFamily="marketingPage"
                  fontWeight={500}
                  fontSize="16px"
                  color="content.title"
                  mb={3}>
                  Role
                </FormLabel>
                <Select
                  value={role}
                  onChange={(e) => setRole(e.target.value as CustomerRole)}
                  bg="surface.panel"
                  border="1px solid"
                  borderColor="surface.border"
                  borderRadius="md"
                  fontSize="16px"
                  fontFamily="marketingPage"
                  _focus={{
                    borderColor: 'icon.green',
                    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                  }}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </Select>
              </FormControl>

              <FormTextInput
                useForm={false}
                label="Display Name (Optional)"
                placeholder="John Doe"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />

              {error && (
                <Text color="red.500" fontSize="sm">
                  {error}
                </Text>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter gap={3} px={6} pb={6}>
            <Button
              type="button"
              bg="surface.sidebarMuted"
              color="content.body"
              size="md"
              px={6}
              py={2}
              borderRadius="md"
              fontWeight="600"
              buttonName="cancel-invite"
              fontFamily="marketingPage"
              onClick={handleClose}
              flex={1}
              _hover={{ bg: 'sidebar.itemHoverBg' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              bg="icon.green"
              color="white"
              size="md"
              px={6}
              py={2}
              borderRadius="md"
              fontWeight="600"
              buttonName="send-invite"
              fontFamily="marketingPage"
              isLoading={isLoading}
              isDisabled={!isValid}
              flex={1}
              _hover={{ bg: 'green.600' }}>
              Send Invite
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
